import { useQuery } from '@tanstack/react-query';
import { fetchProducts, fetchProductByHandle, ShopifyProduct, storefrontApiRequest, NEWEST_PRODUCTS_QUERY } from '@/lib/shopify';

// Brand priority: Snozy > Magic Sauce > Magic Farmers
function getBrandPriority(product: ShopifyProduct): number {
  const title = product.node.title.toLowerCase();
  const vendor = product.node.vendor?.toLowerCase() || '';
  
  if (vendor.includes('snozy') || title.includes('snozy')) return 1;
  if (title.includes('magic sauce') || vendor.includes('magic sauce')) return 2;
  if (vendor.includes('magic farmers') || title.includes('magic farmers')) return 3;
  return 4; // Other products
}

// Sort products: in-stock first, then by brand priority (Snozy > Magic Sauce > Magic Farmers)
function sortByAvailabilityAndBrand(products: ShopifyProduct[]): ShopifyProduct[] {
  return [...products].sort((a, b) => {
    // First: in-stock vs out-of-stock
    const aAvailable = a.node.variants.edges.some(v => v.node.availableForSale);
    const bAvailable = b.node.variants.edges.some(v => v.node.availableForSale);
    
    if (aAvailable && !bAvailable) return -1;
    if (!aAvailable && bAvailable) return 1;
    
    // Second: brand priority (Snozy > Magic Sauce > Magic Farmers)
    const aPriority = getBrandPriority(a);
    const bPriority = getBrandPriority(b);
    
    return aPriority - bPriority;
  });
}

// Check if product is a bundle (pack/bundle/paket)
export function isBundle(product: ShopifyProduct): boolean {
  const title = product.node.title.toLowerCase();
  return title.includes('pack') || title.includes('bundle') || title.includes('paket');
}

// Check if product is in stock
export function isInStock(product: ShopifyProduct): boolean {
  return product.node.variants.edges.some(v => v.node.availableForSale);
}

export function useProducts(first: number = 20, query?: string, sortByStock: boolean = true) {
  return useQuery<ShopifyProduct[]>({
    queryKey: ['products', first, query, sortByStock],
    queryFn: async () => {
      const products = await fetchProducts(first, query);
      return sortByStock ? sortByAvailabilityAndBrand(products) : products;
    },
  });
}

// Fetch all active products sorted by availability and brand
export function useAllProducts(limit: number = 100) {
  return useQuery<ShopifyProduct[]>({
    queryKey: ['all-products', limit],
    queryFn: async () => {
      const products = await fetchProducts(limit);
      return sortByAvailabilityAndBrand(products);
    },
  });
}

// Fetch only single products (non-bundles), sorted by availability and brand
export function useSingleProducts(limit: number = 50) {
  return useQuery<ShopifyProduct[]>({
    queryKey: ['single-products', limit],
    queryFn: async () => {
      const products = await fetchProducts(limit);
      const singleProducts = products.filter(p => !isBundle(p));
      return sortByAvailabilityAndBrand(singleProducts);
    },
  });
}

// Fetch only bundles, sorted by availability
export function useBundles(limit: number = 20) {
  return useQuery<ShopifyProduct[]>({
    queryKey: ['bundles', limit],
    queryFn: async () => {
      const products = await fetchProducts(limit, "title:Pack OR title:Bundle OR title:Paket");
      const bundles = products.filter(p => isBundle(p));
      return sortByAvailabilityAndBrand(bundles);
    },
  });
}

// Fetch all vapes (Snozy first, then Magic Sauce, then Magic Farmers)
export function useAllVapes(limit: number = 20) {
  return useQuery<ShopifyProduct[]>({
    queryKey: ['all-vapes', limit],
    queryFn: async () => {
      const products = await fetchProducts(limit, "title:Vape OR title:Engångsvape");
      const vapes = products.filter(p => {
        const title = p.node.title.toLowerCase();
        const isVape = title.includes('vape') || title.includes('engångsvape');
        return isVape && !isBundle(p);
      });
      return sortByAvailabilityAndBrand(vapes);
    },
  });
}

// Fetch THCA Buds & Hash (non-bundles)
export function useBudsAndHash(limit: number = 20) {
  return useQuery<ShopifyProduct[]>({
    queryKey: ['buds-hash', limit],
    queryFn: async () => {
      const products = await fetchProducts(limit, "product_type:'Premium Blommor' OR product_type:'Premium Hash' OR title:Buds OR title:Hash OR title:'Bubble Hash'");
      const budsHash = products.filter(p => !isBundle(p));
      return sortByAvailabilityAndBrand(budsHash);
    },
  });
}

export function useProduct(handle: string) {
  return useQuery({
    queryKey: ['product', handle],
    queryFn: () => fetchProductByHandle(handle),
    enabled: !!handle,
  });
}

// Check if product is Bubble Hash (pinned product for Nyheter)
export function isBubbleHash(product: ShopifyProduct): boolean {
  const handle = product.node.handle?.toLowerCase() || '';
  const title = product.node.title.toLowerCase();
  return handle.includes('bubble-hash') || title.includes('bubble hash');
}

// Fetch newest products (sorted by created_at, includes out-of-stock)
// Pins Bubble Hash first if found, in-stock products prioritized
export function useNewProducts(limit: number = 12) {
  return useQuery<ShopifyProduct[]>({
    queryKey: ['new-products', limit],
    queryFn: async () => {
      const data = await storefrontApiRequest(NEWEST_PRODUCTS_QUERY, { first: limit });
      const products: ShopifyProduct[] = data?.data?.products?.edges || [];
      
      // Filter non-bundles (keep both in-stock and out-of-stock)
      const filtered = products.filter(p => !isBundle(p));
      
      // Sort: in-stock first, then out-of-stock
      filtered.sort((a, b) => {
        const aAvailable = a.node.variants.edges.some(v => v.node.availableForSale);
        const bAvailable = b.node.variants.edges.some(v => v.node.availableForSale);
        if (aAvailable && !bAvailable) return -1;
        if (!aAvailable && bAvailable) return 1;
        return 0;
      });
      
      // Pin Bubble Hash first if found
      const bubbleHashIndex = filtered.findIndex(p => isBubbleHash(p));
      if (bubbleHashIndex > 0) {
        const [bubbleHash] = filtered.splice(bubbleHashIndex, 1);
        filtered.unshift(bubbleHash);
      }
      
      return filtered;
    },
  });
}
